import React, { useRef, useState } from 'react';
import { CDN2 } from '@/helpers/api';
import { basicPageProps, fetchTemplate } from '@/utils/isServer';
import {
  Breadcrumb,
  Form,
  Input,
  Button,
  Select,
  Radio,
  message,
  Switch,
  DatePicker,
  Modal,
  Upload,
} from 'antd';
import { GetServerSideProps } from 'next';
import Head from 'next/head';
import { UploadOutlined } from '@/components2/Icons';
import ReCAPTCHA from "react-google-recaptcha";
import { modalType } from '@/components/Modal';

const { TextArea } = Input;

export const getServerSideProps: GetServerSideProps = async (ctx) => {
  const { configInfo, airports } = await basicPageProps(ctx);

  return {
    props: {
      airports,
      recaptchaSiteKey: configInfo.recaptchaSiteKey,
    },
  };
};

function Onboarding({recaptchaSiteKey}) {
  const [form] = Form.useForm();
  const [modalVisible, setModalVisible] = useState(false);
  const [formPreviewData, setFormPreviewData] = useState({});
  const [loading, setLoading] = useState<boolean>(false);
  const [file, setFile] = useState<any>(null);
  const captchaRef = useRef(null);

  const handleSubmit = (values: any) => {
    const formattedValues = {
      ...values,
      sellDate: values.sellDate?.format('YYYY-MM-DD') || null,
    };
    setFormPreviewData(formattedValues);
    setModalVisible(true);
  };

  const confirmSubmission = async () => {
    let recaptchaToken = '';
    if (captchaRef.current) {
      try {
        recaptchaToken = await captchaRef.current.executeAsync();
      }
      catch (e) {
          modalType({
            type: 'error',
            title: 'Error',
            content: (
              <div className="text-left">
                <p>Error</p>
                <p>
                  Captcha error
                </p>
              </div>
            ),
          });
        captchaRef.current.reset();
        return;
      }
      captchaRef.current.reset();
    }
    setLoading(true);

    const fd = new FormData();
    fd.append("type", "3")
    fd.append("organization", form.getFieldValue('organization') ?? "")
    fd.append("facilityAddress", form.getFieldValue('facilityAddress') ?? "")
    fd.append("facilityHours", form.getFieldValue('facilityHours') ?? "")
    fd.append("managerName", form.getFieldValue('managerName') ?? "")
    fd.append("managerEmail", form.getFieldValue('managerEmail') ?? "")
    fd.append("managerPhone", form.getFieldValue('managerPhone') ?? "")
    fd.append("parkingType", form.getFieldValue('parkingType') ?? "")
    fd.append("dailyRate", form.getFieldValue('dailyRate') ?? "")
    fd.append("taxes", form.getFieldValue('taxes') ?? "")
    fd.append("arrivalInstructions", form.getFieldValue('arrivalInstructions') ?? "")
    fd.append("departureInstructions", form.getFieldValue('departureInstructions') ?? "")
    fd.append("shuttle", form.getFieldValue('shuttle') ?? "")
    fd.append("shuttlePhone", form.getFieldValue('shuttlePhone') ?? "")
    fd.append("shuttleText", form.getFieldValue('shuttleText') ?? "")
    fd.append("evCharger", form.getFieldValue('evCharger') ?? "")
    fd.append("wheelchair", form.getFieldValue('wheelchair') ?? "")
    fd.append("licensePlate", form.getFieldValue('licensePlate') ?? "")
    fd.append("additionalInstructions", form.getFieldValue('additionalInstructions') ?? "")
    fd.append("parcs", form.getFieldValue('parcs') ?? "")
    fd.append("hasIntegrator", form.getFieldValue('hasIntegrator') ?? "")
    fd.append("notes", form.getFieldValue('notes') ?? "")
    fd.append("sellDate", form.getFieldValue('sellDate') ?? "")
    fd.append("legalEntity", form.getFieldValue('legalEntity') ?? "")
    fd.append("bankName", form.getFieldValue('bankName') ?? "")
    fd.append("routingNumber", form.getFieldValue('routingNumber') ?? "")
    fd.append("accountNumber", form.getFieldValue('accountNumber') ?? "")
    fd.append("inventoryAllocated", form.getFieldValue('inventoryAllocated') ?? "")
    fd.append("facilityName", form.getFieldValue('facilityName') ?? "")
    fd.append("recaptchaToken", recaptchaToken)
    if (file) {
      fd.append("file", file)
    }
    fetch('/ADMMemberAccess/Onboarding', {
      ...fetchTemplate,
      method: 'POST',
      body: fd,
    })
      .then((success) => {
        setLoading(false);
        // setSendingForm(false);
        message.success('Partner application submitted!');
        form.resetFields();
        setModalVisible(false);
      })
      .catch((error) => {
        setLoading(false);
      });
  };

  return (
    <div>
      <Head>
        <title>Onboarding | On Air Parking</title>
        <meta name="robots" content="noindex" />
        <meta
          name="description"
          content="Provide your facility info to get started with On Air Parking and begin selling parking at your location."
          key="description"
        />
        <meta
          property="og:url"
          content="https://www.onairparking.com/onboarding"
          key="og:url"
        />
        <meta property="og:type" content="website" key="og:type" />
        <meta
          property="og:title"
          content="Onboarding | On Air Parking"
          key="og:title"
        />
        <meta
          property="og:description"
          content="Provide your facility info to get started with On Air Parking and begin selling parking at your location."
          key="og:description"
        />
        <meta
          property="og:image"
          content={CDN2() + '/staticmyapp/oglogo-white.png'}
          key="og:image"
        />
      </Head>
      <div className="container mx-auto max-w-6xl mb-4 px-4">
        <Breadcrumb className="text-sm md:text-base mt-4 mb-8">
          <Breadcrumb.Item>
            <a href="/">Home</a>
          </Breadcrumb.Item>
          <Breadcrumb.Item>Onboarding</Breadcrumb.Item>
        </Breadcrumb>
        <h2 className="text-xl lg:text-3xl font-bold">
          On Air Parking Onboarding New Partner Details
        </h2>
        <p className="text-lg">
          Please fill out the form below to help us collect essential details about your facility. Required fields are marked and must be completed before submission. Optional fields may be provided later during onboarding or discovery.
        </p>

        <div className="mb-8">
          <h2 className="text-xl font-semibold my-4">Onboarding Application</h2>
          <div className="bg-white p-6 rounded-lg shadow-md">
            <Form
              form={form}
              layout="vertical"
              onFinish={handleSubmit}
              className="space-y-4"
            >
              <Form.Item
                name="organization"
                label="Organization"
                rules={[{ required: true, message: 'Required' }]}
              >
                <Input />
              </Form.Item>
              <Form.Item
                name="facilityName"
                label="Facility Name"
                rules={[{ required: true, message: 'Required' }]}
              >
                <Input />
              </Form.Item>
              <Form.Item
                name="facilityAddress"
                label="Facility Address"
                rules={[{ required: true, message: 'Required' }]}
              >
                <Input />
              </Form.Item>
              <Form.Item
                name="facilityHours"
                label="Facility Hours of Operations"
                rules={[{ required: true, message: 'Required' }]}
              >
                <Input />
              </Form.Item>
              <Form.Item
                name="managerName"
                label="Facility Manager Name"
                rules={[{ required: true, message: 'Required' }]}
              >
                <Input />
              </Form.Item>
              <Form.Item
                name="managerEmail"
                label="Facility Manager Email"
                rules={[
                  { required: true, message: 'Required' },
                  { type: 'email', message: 'Invalid email' },
                ]}
              >
                <Input />
              </Form.Item>
              <Form.Item
                name="managerPhone"
                label="Facility Manager Phone Number"
                rules={[{ required: true, message: 'Required' }]}
              >
                <Input />
              </Form.Item>
              <Form.Item name="parkingType" label="Parking Type: Airport / Cruise Port / City">
                <Input />
              </Form.Item>
              <Form.Item name="dailyRate" label="Daily Parking Rate">
                <Input prefix="$" />
              </Form.Item>
              <Form.Item name="taxes" label="Taxes/Fees if Any">
                <Input />
              </Form.Item>

              <Form.Item name="arrivalInstructions" label="Instructions for Customers Upon Arrival to facility">
                <TextArea rows={2} />
              </Form.Item>
              <Form.Item name="departureInstructions" label="Instructions for Customers When Departing the facility">
                <TextArea rows={2} />
              </Form.Item>

              <Form.Item name="shuttle" label="Shuttle">
                <Select placeholder="Select" className='border rounded-md'>
                  <Select.Option value="yes">Yes</Select.Option>
                  <Select.Option value="no">No</Select.Option>
                </Select>
              </Form.Item>
              <Form.Item
                name="shuttlePhone"
                label="Shuttle Phone Number"
                rules={[{ required: false }]}
              >
                <Input />
              </Form.Item>

              <Form.Item
                name="shuttleText"
                label="Shuttle Text Number"
                rules={[{ required: false }]}
              >
                <Input />
              </Form.Item>

              <Form.Item name="facilityLogo" label="Facility Logo">
                <Upload
                  listType="picture"
                  beforeUpload={() => false}
                  accept="image/*"
                  maxCount={1}
                  onChange={e => setFile(e.file)}
                  onRemove={e => setFile(null)}
                >
                  <Button icon={<UploadOutlined />}>Click to Upload</Button>
                </Upload>
              </Form.Item>

              <Form.Item name="evCharger" label="EV Charger?">
                <Switch checkedChildren="Yes" unCheckedChildren="No" />
              </Form.Item>

              <Form.Item name="wheelchair" label="Wheelchair Accessible?">
                <Switch checkedChildren="Yes" unCheckedChildren="No" />
              </Form.Item>

              <Form.Item name="licensePlate" label="Request License Plate? Required">
                <Switch checkedChildren="Yes" unCheckedChildren="No" />
              </Form.Item>

              <Form.Item name="additionalInstructions" label="Any additional Instructions for Customers to follow or be aware of">
                <TextArea rows={2} />
              </Form.Item>
              <Form.Item name="parcs" label="Integrator/PARCS Information if Any">
                <Input />
              </Form.Item>

              <Form.Item name="hasIntegrator" label="There is an integrator">
                <Select placeholder="Select"
                  className='border rounded-md'>
                  <Select.Option value="yes">Yes</Select.Option>
                  <Select.Option value="no">No</Select.Option>
                </Select>
              </Form.Item>
              <Form.Item name="notes" label="Other Important Notes">
                <TextArea rows={2} />
              </Form.Item>

              <Form.Item name="sellDate" label="How soon are you hoping to sell parking? DATE">
                <DatePicker format="MM/DD/YYYY" className="w-full" />
              </Form.Item>
              <Form.Item name="legalEntity" label="Legal Entity Name for issuing payments">
                <Input />
              </Form.Item>

              <Form.Item name="bankName" label="Bank Name">
                <Input />
              </Form.Item>
              <Form.Item name="routingNumber" label="Bank Routing Number">
                <Input />
              </Form.Item>

              <Form.Item name="accountNumber" label="Bank Account Number">
                <Input />
              </Form.Item>
              <Form.Item name="inventoryAllocated" label="Inventory Allocated to Sale">
                <Input />
              </Form.Item>

              <Form.Item>
                <Button type="primary" htmlType="submit" className="bg-primary text-white">
                  Submit
                </Button>
              </Form.Item>
            </Form>

            <div className="justify-items-center">
              {!!recaptchaSiteKey && (
                <ReCAPTCHA
                  sitekey={recaptchaSiteKey}
                  ref={captchaRef}
                  size="invisible"
                />
              )}
            </div>

            <Modal
              title="Confirm Your Information"
              visible={modalVisible}
              confirmLoading={loading}
              onOk={confirmSubmission}
              onCancel={() => setModalVisible(false)}
              okText="Confirm & Submit"
              cancelText="Edit"
              width={700}>
              <div className="space-y-2 max-h-[50vh] overflow-y-auto">
                {Object.entries(formPreviewData).map(([key, value]) => (
                  <div key={key} className="border-b pb-2">
                    <strong className="capitalize">
                      {key.replace(/([A-Z])/g, ' $1')}:
                    </strong>{' '}
                    <span>{value?.toString() || '-'}</span>
                  </div>
                ))}
              </div>
            </Modal>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Onboarding;
